import { type ReactNode } from 'react';

import { cn } from '../../lib/cn';
import { CircleCheckBig, decorativeIcon, RefreshCw, WifiOff } from '../../lib/icons';

export interface ConnectivityBandProps {
  /** Whether the device currently has a usable connection. */
  readonly online: boolean;
  /** How many punches are waiting in the IndexedDB queue. */
  readonly queueCount: number;
  /** The state, in a word: "Hors ligne" / "En ligne". Already translated. */
  readonly stateLabel: string;
  /**
   * The queue, as a sentence already pluralised by the caller:
   * "1 pointage en attente" / "3 pointages en attente".
   */
  readonly queueLabel?: string;
  /** Shown when online with an empty queue: "Tout est synchronisé". */
  readonly clearLabel?: string;
  readonly className?: string;
}

/**
 * THE SIGNATURE COMPONENT. A worker's answer to "did my punch go through?"
 *
 * Full-bleed, 48 px, radius 0, directly beneath the header rail, and it PUSHES
 * content down rather than overlaying it — a queued punch that a floating
 * toast covers is a queued punch the worker never saw.
 *
 * It is **amber, not red**, because a queued punch is not an error: it is the
 * product working exactly as designed on a plant floor with no signal. Red here
 * would train workers to ignore the one colour that means "a person has to fix
 * this".
 *
 * It is **persistent** whenever the queue is non-empty — including when the
 * device is back online but the queue has not drained — and it never claims a
 * punch is confirmed when it is only queued. When there is genuinely nothing
 * pending it collapses to a 28 px quiet strip rather than disappearing, so the
 * absence of the band is never ambiguous with the absence of the feature.
 */
export function ConnectivityBand({
  online,
  queueCount,
  stateLabel,
  queueLabel,
  clearLabel,
  className,
}: ConnectivityBandProps) {
  const pending = queueCount > 0;
  /*
   * The glyph follows `online`, not the band's tone.
   *
   * The amber band is shown in two different situations and they are not the
   * same fact: offline (nothing is leaving this phone) and online with a
   * queue (the punches are on their way). Drawing a crossed-out wifi while
   * the band says "En ligne" makes the icon and the word disagree at the
   * exact moment the app is asking to be trusted with somebody's pay.
   */
  const StateIcon = online ? RefreshCw : WifiOff;
  const settled = online && !pending;

  /*
   * ONE element for both states, not two.
   *
   * The band used to return a different node per state, so the moment the
   * queue drained the live region was REMOVED and a fresh one inserted —
   * which announces nothing in any screen reader, because a live region has
   * to exist before its content changes. A worker waiting to hear that their
   * three queued punches went through heard silence and had to navigate back
   * to the band to find out. Same node, changing children.
   */
  return (
    <div
      role="status"
      aria-live="polite"
      className={cn(
        'flex w-full items-center rounded-band',
        settled
          ? 'min-h-band-quiet gap-2 border-b border-hairline bg-surface px-4 text-support text-ink-muted'
          : 'min-h-band gap-3 bg-status-warning px-4 py-2 text-status-warning-fg',
        className,
      )}
    >
      {settled ? (
        <>
          <CircleCheckBig {...decorativeIcon('xs')} className="shrink-0 text-status-success-ink" />
          <span>{clearLabel ?? stateLabel}</span>
        </>
      ) : (
        <>
          <StateIcon {...decorativeIcon('lg')} className="shrink-0" />
          <span className="text-subject">{stateLabel}</span>
          {pending && queueLabel !== undefined ? (
            <>
              <Divider />
              <span className="min-w-0 text-body-compact">{queueLabel}</span>
            </>
          ) : null}
        </>
      )}
    </div>
  );
}

/** The hairline between the state and the queue depth, at 35% of the fg. */
function Divider(): ReactNode {
  return <span aria-hidden="true" className="h-[18px] w-px shrink-0 bg-current opacity-35" />;
}
